import { Dispatch } from "redux";
import { DistributionList, DistributionListAttributes, Meta } from "typings";
import { DistributionLists } from "@pacerpro/api";
import { UPDATE_FREQUENCIES } from "@pacerpro/utils";
import { DistributionListPayload } from "./distributionLists";

type TracksPayload = {
  data: DistributionList[];
  included: any[];
  meta?: Meta;
  links: {
    first: string;
    prev?: string;
    next?: string;
    last: string;
  };
  filteredTotal: number;
};

/////////////////////////////////////////////////
// TRACKS CONSTANTS
/////////////////////////////////////////////////

export const SET_TRACKS = "SET_TRACKS";
export const SET_TRACKS_PAGE = "SET_TRACKS_PAGE";
export const SET_TRACKS_LOADING = "SET_TRACKS_LOADING";
export const SET_TRACKS_NAV_LOADING = "SET_TRACKS_NAV_LOADING";
export const SET_MORE_TRACKS = "SET_MORE_TRACKS";
export const UNSET_TRACKS = "UNSET_TRACKS";
export const EDIT_TRACK = "EDIT_TRACK";
export const EDIT_TRACK_IN_TABLE = "EDIT_TRACK_IN_TABLE";
export const REMOVE_TRACK = "REMOVE_TRACK";

/////////////////////////////////////////////////
// TRACKS ACTIONS
/////////////////////////////////////////////////

export const setTracks = ({
  data = [],
  included = [],
  meta,
  links,
  filteredTotal = 0,
}: TracksPayload) => ({
  type: SET_TRACKS,
  payload: { data, included, meta, links, filteredTotal },
});

export const setMoreTracks = ({
  data = [],
  included = [],
  links,
}: TracksPayload) => ({
  type: SET_MORE_TRACKS,
  payload: { data, included, links },
});

export const unsetTracks = () => {
  type: UNSET_TRACKS;
};

export const setTracksLoading = (payload: boolean) => ({
  type: SET_TRACKS_LOADING,
  payload,
});

export const setTracksNavLoading = (payload: boolean) => ({
  type: SET_TRACKS_NAV_LOADING,
  payload,
});

export const loadMoreTracks = (options: {
  sort?: string;
  filter?: { [key: string]: string | undefined };
  number?: number;
  size?: number;
  include?: string[];
}) => async (dispatch: Dispatch) => {
  try {
    dispatch(setTracksLoading(true));
    const tracksResposne = await DistributionLists.getAll(options);
    dispatch(setTracksLoading(false));
    dispatch({
      type: SET_TRACKS_PAGE,
      payload: options.number,
    });
    dispatch(setMoreTracks(tracksResposne.data));
  } catch (error) {
    console.error(error);
  }
};

export const editTrack = (
  distributionListId: string,
  distributionListAttributes: DistributionListAttributes,
) => async (dispatch: Dispatch) => {
  try {
    const distributionListResponse = await DistributionLists.edit(
      distributionListId,
      distributionListAttributes,
      {
        include: ["case", "members", "members.user"],
      },
    );
    if (
      distributionListResponse.data.data.attributes.updateFrequency ===
      UPDATE_FREQUENCIES.NEVER
    ) {
      return dispatch({
        type: REMOVE_TRACK,
        payload: distributionListResponse.data.data.id,
      });
    }
    return dispatch({
      type: EDIT_TRACK,
      payload: distributionListResponse.data,
    });
  } catch (error) {
    console.error(error);
  }
};

export const editTrackInTable = ({
  data,
  included = [],
}: DistributionListPayload) => ({
  type: EDIT_TRACK_IN_TABLE,
  payload: { data, included },
});

export type TracksAction =
  | {
      type: typeof SET_TRACKS;
      payload: TracksPayload;
    }
  | {
      type: typeof SET_MORE_TRACKS;
      payload: TracksPayload;
    }
  | {
      type: typeof SET_TRACKS_PAGE;
      payload: number;
    }
  | {
      type: typeof SET_TRACKS_LOADING;
      payload: boolean;
    }
  | {
      type: typeof SET_TRACKS_NAV_LOADING;
      payload: boolean;
    }
  | {
      type: typeof EDIT_TRACK_IN_TABLE;
      payload: {
        data: DistributionList;
        included: any[];
      };
    }
  | {
      type: typeof UNSET_TRACKS;
    }
  | {
      type: typeof EDIT_TRACK;
      payload: {
        data: DistributionList;
        included: any[];
      };
    }
  | {
      type: typeof EDIT_TRACK_IN_TABLE;
      payload: {
        data: DistributionList;
        included: any[];
      };
    }
  | {
      type: typeof REMOVE_TRACK;
      payload: string;
    };

/////////////////////////////////////////////////
// TRACKS REDUCER
/////////////////////////////////////////////////

export interface TracksState {
  page: number;
  isLoading: boolean;
  navLoading: boolean;
  filteredTotal: number;
  tracks: {
    data: DistributionList[];
    included: any[];
    meta: Meta;
    links?: {
      first: string;
      prev?: string;
      next?: string;
      last: string;
    };
  };
}

const initialState: TracksState = {
  page: 1,
  isLoading: false,
  navLoading: false,
  filteredTotal: 0,
  tracks: {
    data: [],
    included: [],
    meta: {
      recordCount: 0,
      pageCount: 0,
    },
    links: {
      first: "",
      last: "",
    },
  },
};

export default (state = initialState, action: TracksAction) => {
  switch (action.type) {
    case SET_TRACKS: {
      return {
        ...state,
        page: 1,
        isLoading: false,
        filteredTotal: action.payload.filteredTotal,
        tracks: {
          data: action.payload.data,
          included: action.payload.included,
          meta: action.payload.meta || state.tracks.meta,
        },
      };
    }
    case SET_MORE_TRACKS: {
      return {
        ...state,
        tracks: {
          ...state.tracks,
          data: [...state.tracks.data, ...action.payload.data],
          included: [...state.tracks.included, ...action.payload.included],
          meta: action.payload.meta || state.tracks.meta,
        },
      };
    }
    case UNSET_TRACKS: {
      return {
        ...state,
        tracks: initialState.tracks,
      };
    }
    case SET_TRACKS_PAGE: {
      return {
        ...state,
        page: action.payload,
      };
    }
    case SET_TRACKS_LOADING: {
      return {
        ...state,
        isLoading: action.payload,
      };
    }
    case SET_TRACKS_NAV_LOADING: {
      return {
        ...state,
        navLoading: action.payload,
      };
    }
    case EDIT_TRACK: {
      return {
        ...state,
        tracks: {
          ...state.tracks,
          data: state.tracks.data.map(track =>
            track.id === action.payload.data.id ? action.payload.data : track,
          ),
        },
      };
    }
    case EDIT_TRACK_IN_TABLE: {
      return {
        ...state,
        tracks: {
          ...state.tracks,
          data: state.tracks.data.map(track =>
            track.id === action.payload.data.id ? action.payload.data : track,
          ),
        },
      };
    }
    case REMOVE_TRACK: {
      return {
        ...state,
        tracks: {
          ...state.tracks,
          data: state.tracks.data.filter(track => track.id !== action.payload),
        },
        filteredTotal: state.filteredTotal - 1,
      };
    }
    default:
      return state;
  }
};
